import { Trans } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import {
  GqlDocSystemTagKind,
  GqlMeeting,
  type GqlMeetingConcern,
  type GqlShareIssue,
} from '../../../../graphql/base-types.graphql';
import { DisplayDocument, Document } from '@startuptools/business-ui/document/Document';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { DocGenRelationName } from '@startuptools/common/doc-gen';
import { apiClient } from '../../../../common/http';
import { useApolloClient } from '@apollo/client';
import { ShareIssueDocument } from '../../ShareIssue.graphql';
import { useStepper } from '../../../../components/steppers/common/StepperContext';

const getDefaultFilename = (
  mc: Pick<GqlMeetingConcern, 'appendixNumber'> | null | undefined,
  shareIssue: Pick<GqlShareIssue, 'paymentKindOffset' | 'paymentKindNonCash'>,
) => {
  let appendixName = `${mc?.appendixNumber ?? 'x'}.3`;
  if (shareIssue.paymentKindOffset && shareIssue.paymentKindNonCash) {
    appendixName = `${mc?.appendixNumber ?? 'x'}.5`;
  } else if (shareIssue.paymentKindNonCash || shareIssue.paymentKindOffset) {
    appendixName = `${mc?.appendixNumber ?? 'x'}.4`;
  }
  return `Appendix ${appendixName} - Revisorns yttrande angående styrelsens redogörelse.pdf`;
};

export const SiStepAuditorStatement = ({
  shareIssue,
}: {
  shareIssue: Pick<GqlShareIssue, 'id' | 'paymentKindNonCash' | 'paymentKindOffset'> & {
    meetingConcern?:
      | (Pick<GqlMeetingConcern, 'id' | 'appendixNumber'> & {
          meeting: Pick<GqlMeeting, 'id'>;
        })
      | null;
    latestAuditorStatement?: DisplayDocument | null;
  };
}) => {
  const companyId = useCompanyId();
  const apolloClient = useApolloClient();
  const { stepNext } = useStepper();

  const defaultFilename = getDefaultFilename(shareIssue.meetingConcern, shareIssue);

  return (
    <Stack gap={3}>
      <Typography level="h4">
        <Trans id="Upload auditor statement" />
      </Typography>

      <Document
        defaultFileName={defaultFilename}
        document={shareIssue.latestAuditorStatement}
        upload={async data => {
          const file = data.file;
          const relation = {
            relation_id: shareIssue.id,
            relation_name: DocGenRelationName.ShareIssueId,
          };
          await apiClient.uploadFile(
            file,
            file.name,
            companyId,
            relation,
            false,
            GqlDocSystemTagKind.AuditorsStatement,
          );
          return apolloClient.refetchQueries({
            include: [ShareIssueDocument],
          });
        }}
      />

      <Box>
        <Button onClick={() => stepNext()}>
          <Trans id="Continue" />
        </Button>
      </Box>
    </Stack>
  );
};
